import { useState } from "react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";

interface InsightTagFilterProps {
  tags: string[];
  selectedTags: string[];
  onToggleTag: (tag: string) => void;
  language: string;
}

// mobile ~3 per row × 3 rows; desktop ~7–8 per row × 2 rows
const MOBILE_VISIBLE_COUNT = 9;
const DESKTOP_VISIBLE_COUNT = 16;

const InsightTagFilter = ({
  tags,
  selectedTags,
  onToggleTag,
  language
}: InsightTagFilterProps) => {
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [desktopExpanded, setDesktopExpanded] = useState(false);

  const allTags = tags;
  const mobileHiddenCount = allTags.length - MOBILE_VISIBLE_COUNT;
  const desktopHiddenCount = allTags.length - DESKTOP_VISIBLE_COUNT;
  const mobileVisibleTags = mobileExpanded ? allTags : allTags.slice(0, MOBILE_VISIBLE_COUNT);
  const desktopVisibleTags = desktopExpanded ? allTags : allTags.slice(0, DESKTOP_VISIBLE_COUNT);

  const tagButton = (tag: string) => (
    <motion.div
      key={tag}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
    >
      <Button
        variant={selectedTags.includes(tag) ? "default" : "outline"}
        size="sm"
        onClick={() => onToggleTag(tag)}
        className={`text-xs px-3 h-8 ${
          selectedTags.includes(tag)
            ? 'bg-white text-primary border-2 border-primary hover:bg-primary/5'
            : 'bg-white/80 text-neutral-600 border hover:bg-white'
        }`}
      >
        {tag}
      </Button>
    </motion.div>
  );

  const allButton = (extraClass = "") => (
    <Button
      variant={selectedTags.length === 0 ? "default" : "outline"}
      size="sm"
      onClick={() => onToggleTag('all')}
      className={`text-xs px-3 h-8 ${extraClass} ${
        selectedTags.length === 0
          ? 'bg-white text-primary border-2 border-primary hover:bg-primary/5'
          : 'bg-white/80 text-neutral-600 border hover:bg-white'
      }`}
    >
      {language === 'KO' ? '전체' : 'All'}
    </Button>
  );

  const toggleLink = (expanded: boolean, hiddenCount: number, onToggle: () => void) => (
    hiddenCount > 0 && (
      <div className="flex justify-center mt-2">
        <button
          onClick={onToggle}
          className="flex items-center gap-1 text-xs font-medium text-primary/80 hover:text-primary bg-white/70 hover:bg-white/90 px-3 py-1 rounded-full border border-primary/20 transition-colors"
        >
          {expanded ? (
            <><ChevronUp className="h-3.5 w-3.5" />{language === 'KO' ? '접기' : 'See less'}</>
          ) : (
            <><ChevronDown className="h-3.5 w-3.5" />{language === 'KO' ? `+${hiddenCount}개 더보기` : `+${hiddenCount} more`}</>
          )}
        </button>
      </div>
    )
  );

  return (
    <motion.div
      className="mt-4 animate-fade-up [animation-delay:700ms]"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Mobile */}
      <div className="md:hidden">
        <div className="flex flex-wrap gap-1.5 justify-center">
          {allButton()}
          <AnimatePresence mode="popLayout">
            {mobileVisibleTags.map((tag) => tagButton(tag))}
          </AnimatePresence>
        </div>
        {toggleLink(mobileExpanded, mobileHiddenCount, () => setMobileExpanded((v) => !v))}
      </div>

      {/* Desktop */}
      <div className="hidden md:block">
        <div className="flex flex-wrap gap-2 justify-center">
          {allButton()}
          <AnimatePresence mode="popLayout">
            {desktopVisibleTags.map((tag) => tagButton(tag))}
          </AnimatePresence>
        </div>
        {toggleLink(desktopExpanded, desktopHiddenCount, () => setDesktopExpanded((v) => !v))}
      </div>
    </motion.div>
  );
};

export default InsightTagFilter;